<template>
  <v-dialog
    v-model="isOpen"
		fullscreen
    scrollable
  >
    <v-card v-if="item" class="fm-document-viewer pa-0">
      <div class="d-flex align-center justify-space-between fm-document-header">
				<img :src="require('@/assets/Svg/logoWhite.svg')" style="max-width: 110px; width: 100%;"/>
				<div class="d-flex align-center">
					<v-icon class="mr-3" color="white" v-if="!isMobile">{{ getFileIcon }}</v-icon>
					<div class="font-weight-medium text-truncate" v-if="!isMobile">{{ item.name }}</div>
				</div>
				<div class="d-flex align-center" style="gap: 8px;">
					<v-chip small outlined color="white" label v-if="!isMobile">{{ getFileInfo }}</v-chip>
					<v-tooltip top>
						<template v-slot:activator="{ on, attrs }">
							<v-btn
								icon
								small
								@click="associationsOpen = !associationsOpen"
								color="white"
								v-bind="attrs"
								v-on="on"
							>
								<v-icon>mdi-link-variant</v-icon>
							</v-btn>
						</template>
						<span>Asociaciones</span>
					</v-tooltip>
          <RevisionFileSelector
            :revision-context="revisionContext"
            :current-file-id="item.id"
            :loading="revisionLoading"
            @select="$emit('select-revision', $event)"
          />
					<div v-if="openMorphableText">
						<v-tooltip top>
							<template v-slot:activator="{ on, attrs }">
								<v-btn
									icon
									small
									color="white"
									@click="$emit('open-morphable', item)"
									v-bind="attrs"
									v-on="on"
								>
									<v-icon>{{ openMorphableIcon }}</v-icon>
								</v-btn>
							</template>
							<span>{{ openMorphableText }}</span>
						</v-tooltip>
					</div>
					<v-btn icon small @click="openInNewTab" class="ml-1" color="white">
						<v-icon>mdi-open-in-new</v-icon>
					</v-btn>
					<div>
						<v-tooltip top>
							<template v-slot:activator="{ on, attrs }">
								<v-btn
									icon
									small
									:href="item.pathdoc?.path"
									target="_blank"
									color="white"
									v-bind="attrs"
									v-on="on"
								>
									<v-icon>mdi-download</v-icon>
								</v-btn>
							</template>
							<span>Descargar archivo</span>
						</v-tooltip>
					</div>
					<v-btn icon small @click="$emit('minimize-file', item)" class="ml-1" color="white">
						<v-icon>mdi-minus</v-icon>
					</v-btn>
					<v-btn icon small @click="closeDialog" class="ml-1" color="white">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</div>
      </div>
      <v-card-text class="fm-document-content pa-0">
        <!-- Visor PDF con EmbedPDF -->
        <div
          v-if="isPdf"
          :key="'pdf-' + item.id"
          ref="pdfViewerContainer"
          class="pdf-viewer-container"
        >
          <div v-if="isLoadingPdfViewer" class="text-center py-8">
            <v-progress-circular indeterminate color="primary" />
            <p class="text-caption mt-2">Cargando visor de PDF...</p>
          </div>
          <div v-if="pdfLoadError" class="text-center py-8">
            <p class="text-subtitle1 mb-4">No se pudo cargar el visor de PDF</p>
            <p class="text-caption mb-4 text-grey">
              {{ item.name }} ({{ item.ext?.toUpperCase() }})
            </p>
            <v-btn
              :href="item.pathdoc?.path"
              target="_blank"
              color="primary"
              outlined
            >
              <v-icon left>mdi-download</v-icon>
              Descargar archivo
            </v-btn>
          </div>
        </div>

        <!-- Visor genérico (Office Online / Google Docs Viewer) para el resto de formatos -->
        <div v-else-if="documentUrl" class="document-viewer-container">
          <iframe
            :src="documentUrl"
            frameborder="0"
            width="100%"
            height="600px"
            allow="fullscreen"
            class="document-viewer-iframe"
            @error="onIframeError"
            @load="onIframeLoad"
          ></iframe>
        </div>

        <div v-else class="text-center py-8">
          <p class="text-subtitle1 mb-4">
            {{ loadFailed ? 'No se pudo cargar la vista previa del documento' : 'No se puede cargar la vista previa' }}
          </p>
          <p class="text-caption mb-4 text-grey">
            {{ item.name }} ({{ item.ext?.toUpperCase() }})
          </p>
          <v-btn
            :href="item.pathdoc?.path"
            target="_blank"
            color="primary"
            outlined
          >
            <v-icon left>mdi-download</v-icon>
            Descargar archivo
          </v-btn>
        </div>
      </v-card-text>
			<AssociationsListModal v-model="associationsOpen" :file-id="item.id" />
    </v-card>
  </v-dialog>
</template>

<script>
import AssociationsListModal from '@/components/Globals/Helpers/AssociationsListModal'
import RevisionFileSelector from '@/components/Globals/Helpers/RevisionFileSelector'

// Cache a nivel de módulo: el import dinámico sólo se dispara una vez por sesión,
// las siguientes aperturas de PDF reutilizan la misma promesa (ya resuelta/cacheada).
let embedPdfModulePromise = null
function loadEmbedPdfModule() {
  if (!embedPdfModulePromise) {
    embedPdfModulePromise = import(
      /* webpackIgnore: true */ 'https://cdn.jsdelivr.net/npm/@embedpdf/snippet@2/dist/embedpdf.js'
    )
  }
  return embedPdfModulePromise
}

export default {
  name: 'DocumentViewer',
  components: { AssociationsListModal, RevisionFileSelector },
  props: {
    item: {
      type: Object,
      default: null
    },
    revisionContext: {
      type: Object,
      default: null
    },
    revisionLoading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loadFailed: false,
      isOpen: false,
      isLoadingPdfViewer: false,
      pdfLoadError: false,
			associationsOpen: false
    }
  },
  watch: {
    item(newVal) {
      if (newVal) {
        this.isOpen = true
        this.pdfLoadError = false
				this.associationsOpen = false
        if (this.isPdf) {
          this.$nextTick(() => this.loadPdfViewer())
        }
      }
    }
  },
  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.mobile
    },
    isPdf() {
      const ext = this.item?.ext?.toLowerCase()
      const mimetype = this.item?.pathdoc?.mimetype || ''
      return ext === 'pdf' || mimetype.includes('pdf')
    },
    documentUrl() {
      if (!this.item?.pathdoc?.path || this.loadFailed) {
        return null
      }

      const s3Url = this.item.pathdoc.path
      const ext = this.item.ext?.toLowerCase()
      const mimetype = this.item.pathdoc?.mimetype || ''

      // PDFs: se manejan aparte con EmbedPDF (ver isPdf / loadPdfViewer)
      if (ext === 'pdf' || mimetype.includes('pdf')) {
        return s3Url
      }

      // Documentos Office (Word, Excel, PowerPoint): usar Microsoft Office Online
      if (this.isOfficeDocument(ext, mimetype)) {
        return this.getOfficeUrl(s3Url)
      }

      // Fallback a Google Docs Viewer
      return this.getGoogleViewerUrl(s3Url)
    },
    getFileIcon() {
      const ext = this.item?.ext?.toLowerCase()
      const iconMap = {
        'pdf': 'mdi-file-pdf-box',
        'doc': 'mdi-file-word',
        'docx': 'mdi-file-word',
        'xls': 'mdi-file-excel',
        'xlsx': 'mdi-file-excel',
        'ppt': 'mdi-file-powerpoint',
        'pptx': 'mdi-file-powerpoint'
      }
      return iconMap[ext] || 'mdi-file-document'
    },
    getFileInfo() {
      const ext = this.item?.ext?.toUpperCase() || 'ARCHIVO'
      const size = this.getFileSize()
      return `${ext}${size ? ` • ${size}` : ''}`
    },
		openMorphableText(){
			if(!this.item || !this.item.morphable_type) return null
			if(this.item.morphable_type === 'App\\Models\\TasksRestrictions' || this.item.morphable_type === 'App\\Models\\TasksRestrictionsInteractions'){
				return 'Abrir Tarea Asociada'
			}
			else if(this.item.morphable_type === 'App\\Models\\Rdi' || this.item.morphable_type === 'App\\Models\\RdiInteractions'){
				return 'Abrir RDI Asociada'
			}
			else if(this.item.morphable_type === 'App\\Models\\Additional' || this.item.morphable_type === 'App\\Models\\AdditionalRevision' || this.item.morphable_type === 'App\\Models\\AdditionalObservation'){
				return 'Abrir Adicional Asociado'
			}
			return null
		},
		openMorphableIcon(){
			if(!this.item || !this.item.morphable_type) return null
			if(this.item.morphable_type === 'App\\Models\\TasksRestrictions' || this.item.morphable_type === 'App\\Models\\TasksRestrictionsInteractions'){
				return 'mdi-clipboard-text-outline'
			}
			else if(this.item.morphable_type === 'App\\Models\\Rdi' || this.item.morphable_type === 'App\\Models\\RdiInteractions'){
				return 'mdi-file-document-multiple-outline'
			}
			else if(this.item.morphable_type === 'App\\Models\\Additional' || this.item.morphable_type === 'App\\Models\\AdditionalRevision' || this.item.morphable_type === 'App\\Models\\AdditionalObservation'){
				return 'mdi-notebook-plus-outline'
			}
			return null
		}
  },
	mounted() {
		if (this.item) {
			this.isOpen = true
			if (this.isPdf) {
				this.$nextTick(() => this.loadPdfViewer())
			}
		}
	},
  methods: {
		openInNewTab() {
			if (this.item) {
				const width = window.outerWidth;
				const height = window.outerHeight;
				const left = window.screenX;
				const top = window.screenY;
				const url = 'document-viewer?item_id=' + this.item.id;
				window.open(
					url,
					'_blank',
					`width=${width},height=${height},left=${left},top=${top},noopener,noreferrer`
				);
				this.closeDialog();
			}
		},
    isOfficeDocument(ext, mimetype) {
      const officeExtensions = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx']
      const officeTypes = [
        'application/msword',
        'application/vnd.ms-excel',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument'
      ]

      return (
        officeExtensions.includes(ext) ||
        officeTypes.some(type => mimetype.includes(type))
      )
    },

    getOfficeUrl(s3Url) {
      // Microsoft Office Online Viewer
      const encodedUrl = encodeURIComponent(s3Url)
      const viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`
      console.log('Loading Office document:', { s3Url, viewerUrl })
      return viewerUrl
    },

    getGoogleViewerUrl(s3Url) {
      // Google Docs Viewer como fallback
      const baseUrl = s3Url.split('?')[0]
      const viewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(baseUrl)}&embedded=true`
      console.log('Loading via Google Docs:', { baseUrl, viewerUrl })
      return viewerUrl
    },

    async loadPdfViewer() {
      if (!this.$refs.pdfViewerContainer || !this.item?.pathdoc?.path) return

      this.isLoadingPdfViewer = true
      this.pdfLoadError = false

      try {
        const module = await loadEmbedPdfModule()
        const EmbedPDF = module.default

        EmbedPDF.init({
          type: 'container',
          target: this.$refs.pdfViewerContainer,
          src: this.item.pathdoc.path,
          theme: { preference: 'system' },
          // Un solo documento por visor, sin pestañas
          tabBar: 'never',
          documentManager: { maxDocuments: 1 },
          // Deja sólo lo necesario para ver y buscar: zoom, scroll, selección/búsqueda.
          // Apaga anotaciones, formularios, redacción, sellos/firma, impresión y exportación.
          disabledCategories: [
            'annotation',
            'redaction',
            'forms',
            'stamp',
            'print',
            'export',
            'history',
						'insert',
						'form'
          ],
          i18n: {
            defaultLocale: 'es',
            fallbackLocale: 'en'
          }
        })
      } catch (err) {
        console.error('Error loading EmbedPDF:', err)
        this.pdfLoadError = true
      } finally {
        this.isLoadingPdfViewer = false
      }
    },

    onIframeError() {
      console.error('Error loading document in iframe')
      this.loadFailed = true
      this.$emit('error')
    },

    onIframeLoad() {
      this.loadFailed = false
      this.$emit('load')
    },

    closeDialog() {
      this.isOpen = false
      this.$emit('close')
    },
    getFileSize() {
      const size = this.item?.size
      if (!size) return null
      if (size < 1024) return `${size} B`
      if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`
      return `${(size / (1024 * 1024)).toFixed(2)} MB`
    }
  }
}
</script>

<style scoped>
.fm-document-viewer {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.fm-document-header {
  border-bottom: 1px solid #213872;
  padding: 8px 16px !important;
  background: #2D4B95;
	color: white;
}

.fm-document-header .v-toolbar__content {
  padding: 0 !important;
}

.fm-document-content {
  flex: 1;
  overflow-y: auto;
  background: #fafafa;
}

.document-viewer-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.document-viewer-iframe {
  width: 100%;
  height: 100%;
  background: white;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  border-radius: 6px;
  border: none;
}

.pdf-viewer-container {
  width: 100%;
  height: 100%;
  min-height: 600px;
}
</style>